import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { clearDrafts, rememberReturn } from "@/lib/drafts";

const EXPIRED_TEXT = {
  idle: "Deine Sitzung ist nach 30 Minuten ohne Aktivität abgelaufen. Offene Eingaben sind in diesem Tab als Entwurf gesichert. Bitte melde dich erneut an.",
  absolute: "Deine Sitzung hat die Höchstdauer von 8 Stunden erreicht. Offene Eingaben sind in diesem Tab als Entwurf gesichert. Bitte melde dich erneut an.",
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data.user))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    const expired = (e) => {
      setUser((current) => {
        if (current) {
          rememberReturn();
          toast.error(EXPIRED_TEXT[e.detail?.reason] || "Deine Sitzung ist abgelaufen oder wurde beendet. Bitte melde dich erneut an.",
            { id: "session-expired" });
        }
        return false;
      });
    };
    window.addEventListener("control:session-expired", expired);
    return () => window.removeEventListener("control:session-expired", expired);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return data; // { twofa_required, pending_token }
  }, []);

  const verify2fa = useCallback(async (pendingToken, code) => {
    const { data } = await api.post("/auth/verify-2fa", { pending_token: pendingToken, code });
    if (!data.recovery_codes?.length) setUser(data.user);
    return data;
  }, []);

  const confirmSetup = useCallback(async (pendingToken, code) => {
    // Does NOT set the user here: the Login page shows the one-time recovery codes
    // first, then finalizes the session via setUser(data.user).
    const { data } = await api.post("/auth/2fa/confirm-setup", { pending_token: pendingToken, code });
    return data;
  }, []);

  const recover2fa = useCallback(async (pendingToken, recoveryCode) => {
    const { data } = await api.post("/auth/2fa/recover", {
      pending_token: pendingToken, recovery_code: recoveryCode,
    });
    return data; // { setup_required, pending_token, otpauth_uri, secret, qr_data_url }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* ignore */
    }
    clearDrafts();
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, login, verify2fa, confirmSetup, recover2fa, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

